<script lang="ts">
	/**
	 * Input field component. Allows custom text or a global text key to be set as label.
	 * To use this component, bind your variable to the value of this field.
	 *
	 * <Input bind:value={yourVariable} label="First name" />
	 * <Input bind:value={yourVariable} labelKey="first_name" placeholderKey="first_name" />
	 */
	import Text from '$lib/components/Text.svelte';
	import { page } from '$app/stores';

	interface Translation {
		isRichtext: boolean;
		text: string;
	}

	export let value: string; // Value of the input field
	export let id: string; // Id of the input field, needed for label
	export let type: 'text' | 'password' | 'email' | 'number' | 'date' = 'text'; // Type of the input field
	export let name: string; // (Optional) Name of the inputfield

	export let label: string; // (Optional) Label Text
	export let labelKey: string | null = null; // (Optional) Global text key for label

	export let placeholder: string; // (Optional) Playerholder text
	export let placeholderKey: string | null = null; // (Optional) Global text key for placeholder

	let placeholderText: Translation | null = null;

	$: if (placeholderKey && placeholderKey in $page.data.globalTexts) {
		placeholderText = $page.data.globalTexts[placeholderKey];
	} else {
		placeholderText = {
			isRichtext: false,
			text: '[' + placeholderKey + ']'
		};
	}
</script>

<div class="input">
	{#if label}
		<label for={id}>{label}</label>
	{/if}
	{#if labelKey}
		<label for={id}><Text key={labelKey} /></label>
	{/if}
	{#if type === 'text'}
		<input
			bind:value
			placeholder={placeholderKey ? placeholderText?.text : placeholder}
			type="text"
			{id}
			{name}
		/>
	{/if}
	{#if type === 'password'}
		<input
			bind:value
			placeholder={placeholderKey ? placeholderText?.text : placeholder}
			type="password"
			{id}
			{name}
		/>
	{/if}
	{#if type === 'email'}
		<input
			bind:value
			placeholder={placeholderKey ? placeholderText?.text : placeholder}
			type="email"
			{id}
			{name}
		/>
	{/if}
	{#if type === 'number'}
		<input
			bind:value
			placeholder={placeholderKey ? placeholderText?.text : placeholder}
			type="number"
			{id}
			{name}
		/>
	{/if}
	{#if type === 'date'}
		<input
			bind:value
			placeholder={placeholderKey ? placeholderText?.text : placeholder}
			type="date"
			{id}
			{name}
		/>
	{/if}
</div>

<style lang="scss">
	label {
		display: block;
		margin: 0.5rem;
	}

	input {
		width: 100%;
		padding: 0.6rem;
		display: inline-block;
		border: none;
		border-bottom: 1px solid color(grey_05);
		max-width: 800px;
		@include font(text-sm);
		margin-bottom: v(content-padding);
	}
</style>
